import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

/**
 * SchemaEditor — inline editor for a single category's measurement_schema.
 * Optimistic local state; commits to parent via onSave(rows).
 */
export default function SchemaEditor({ cat, onSave }) {
  const [rows, setRows] = useState(() => (cat.measurement_schema || []).map((f) => ({ ...f })));
  const [newLabel, setNewLabel] = useState("");
  const [newKind, setNewKind] = useState("length");

  // Keep local rows in sync when the parent reloads after a save.
  useEffect(() => {
    setRows((cat.measurement_schema || []).map((f) => ({ ...f })));
  }, [cat.measurement_schema]);

  const addRow = () => {
    const label = newLabel.trim();
    if (!label) return;
    const draftKey = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || `field_${rows.length + 1}`;
    if (rows.some((r) => r.key === draftKey)) return;
    setRows([...rows, { key: draftKey, label, unit_kind: newKind }]);
    setNewLabel("");
    setNewKind("length");
  };

  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div className="mt-3 ml-6 border-l-2 border-brass/40 pl-4 py-3 bg-linen/50" data-testid={`schema-editor-${cat.slug}`}>
      <p className="editorial-label mb-3">measurement fields · {cat.name.toLowerCase()}</p>
      {rows.length === 0 ? (
        <p className="text-[0.65rem] tracking-widest uppercase text-taupe italic">no typed fields. inventory will show the free-text measurements field.</p>
      ) : (
        <ul className="space-y-2 mb-3" data-testid={`schema-rows-${cat.slug}`}>
          {rows.map((r, i) => (
            <li key={`${r.key}-${i}`} className="flex items-center gap-2" data-testid={`schema-row-${cat.slug}-${r.key}`}>
              <input
                type="text"
                value={r.label}
                onChange={(e) => updateRow(i, { label: e.target.value })}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-transparent border-b border-taupe py-1 text-sm text-charcoal focus:outline-none focus:border-brass"
                data-testid={`schema-label-${cat.slug}-${r.key}`}
              />
              <select
                value={r.unit_kind}
                onChange={(e) => updateRow(i, { unit_kind: e.target.value })}
                className="bg-transparent border-b border-taupe py-1 text-[0.65rem] tracking-widest uppercase text-charcoal focus:outline-none"
                data-testid={`schema-kind-${cat.slug}-${r.key}`}
              >
                <option value="length">length (cm/in)</option>
                <option value="free">free text</option>
              </select>
              <button
                onClick={() => removeRow(i)}
                className="p-1.5 border border-taupe text-charcoal hover:border-red-700 hover:text-red-700"
                aria-label="remove"
                data-testid={`schema-remove-${cat.slug}-${r.key}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRow(); } }}
          placeholder="new field label (e.g. neck)"
          data-testid={`schema-new-label-${cat.slug}`}
          className="flex-1 bg-transparent border-b border-taupe py-1 text-sm text-charcoal focus:outline-none focus:border-brass"
        />
        <select
          value={newKind}
          onChange={(e) => setNewKind(e.target.value)}
          data-testid={`schema-new-kind-${cat.slug}`}
          className="bg-transparent border-b border-taupe py-1 text-[0.65rem] tracking-widest uppercase text-charcoal focus:outline-none"
        >
          <option value="length">length</option>
          <option value="free">free</option>
        </select>
        <button
          onClick={addRow}
          disabled={!newLabel.trim()}
          className="border border-taupe text-charcoal hover:border-brass hover:text-brass p-1.5 disabled:opacity-40"
          aria-label="add field"
          data-testid={`schema-add-${cat.slug}`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
      <div className="flex justify-end mt-4">
        <button
          onClick={() => onSave(rows)}
          className="bg-brass hover:bg-espresso text-ivory px-4 py-1.5 text-[0.65rem] tracking-[0.25em] uppercase"
          data-testid={`schema-save-${cat.slug}`}
        >
          save fields
        </button>
      </div>
    </div>
  );
}
